import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useWebAudio } from './useWebAudio'

/**
 * Web Audio APIとHTML5 audioを速度に応じて自動切り替えするhook
 *
 * - playbackSpeed === 1.0: Web Audio API（高速シーク対応）
 * - playbackSpeed !== 1.0: HTML5 audio（playbackRateサポート）
 *
 * iOS Safari対策: Web Audio APIが失敗した場合、自動的にHTML5 audioにフォールバック
 */
export const useAdaptiveAudio = (url: string | null, playbackSpeed: number) => {
  // HTML5 audio用のstate
  const [html5IsReady, setHtml5IsReady] = useState(false)
  const [html5IsPlaying, setHtml5IsPlaying] = useState(false)
  const [html5CurrentTime, setHtml5CurrentTime] = useState(0)
  const [html5Duration, setHtml5Duration] = useState(0)
  const [forceHTML5Mode, setForceHTML5Mode] = useState(false) // Web Audio失敗時の強制HTML5モード

  const html5AudioRef = useRef<HTMLAudioElement | null>(null)
  const pendingPlayRef = useRef<{ offset?: number } | null>(null) // フォールバック時の再生パラメータ保存
  const pendingModeSwitchRef = useRef<{ wasPlaying: boolean; savedTime: number; newSpeed: number } | null>(null) // モード切替時の状態保存

  // Web Audio API
  const {
    isReady: isAudioReady,
    isPlaying: webAudioIsPlaying,
    duration: audioDuration,
    currentTime: audioCurrentTime,
    play: webAudioPlay,
    pause: webAudioPause,
    seek: webAudioSeek,
    initAudioContext,
    error: webAudioError,
  } = useWebAudio(url)

  // 統一インターフェース: playbackSpeedに応じてモードを判定（メモ化）
  // iOS対策: Web Audioが失敗した場合は強制的にHTML5モード
  const useWebAudioMode = useMemo(() => !forceHTML5Mode && playbackSpeed === 1.0, [forceHTML5Mode, playbackSpeed])
  const isReady = useMemo(
    () => (useWebAudioMode ? isAudioReady : html5IsReady),
    [useWebAudioMode, isAudioReady, html5IsReady],
  )
  const isPlaying = useMemo(
    () => (useWebAudioMode ? webAudioIsPlaying : html5IsPlaying),
    [useWebAudioMode, webAudioIsPlaying, html5IsPlaying],
  )
  const duration = useMemo(
    () => (useWebAudioMode ? audioDuration : html5Duration),
    [useWebAudioMode, audioDuration, html5Duration],
  )
  const currentTime = useMemo(
    () => (useWebAudioMode ? audioCurrentTime : html5CurrentTime),
    [useWebAudioMode, audioCurrentTime, html5CurrentTime],
  )

  // 統一されたplay関数（Web Audio失敗時はHTML5にフォールバック）
  const play = useCallback(
    async (offset?: number) => {
      if (useWebAudioMode) {
        try {
          await webAudioPlay(offset)
          return
        } catch (error) {
          // Web Audio再生失敗時はHTML5モードに切り替え
          console.warn('[useAdaptiveAudio] Web Audio failed, falling back to HTML5 audio:', error)

          // 再生パラメータを保存（useEffectで自動再試行）
          pendingPlayRef.current = { offset }
          console.log('[useAdaptiveAudio] Saved pending play request, switching to HTML5 mode...')

          setForceHTML5Mode(true)
          return
        }
      }

      // HTML5 audioモード
      if (!html5AudioRef.current) {
        console.error('[useAdaptiveAudio] HTML5 audio element not available')
        return
      }

      console.log('[useAdaptiveAudio] Playing with HTML5 audio, currentTime:', html5AudioRef.current.currentTime)
      if (offset !== undefined) {
        html5AudioRef.current.currentTime = offset
        console.log('[useAdaptiveAudio] Set offset to:', offset)
      }
      try {
        await html5AudioRef.current.play()
        console.log('✅ [useAdaptiveAudio] HTML5 audio play successful!')
      } catch (error) {
        console.error('❌ [useAdaptiveAudio] HTML5 audio play failed:', error)
      }
    },
    [useWebAudioMode, webAudioPlay],
  )

  // 統一されたpause関数
  const pause = useCallback(() => {
    if (useWebAudioMode) {
      webAudioPause()
      return
    }

    html5AudioRef.current?.pause()
  }, [useWebAudioMode, webAudioPause])

  // 統一されたseek関数
  const seek = useCallback(
    (time: number) => {
      if (useWebAudioMode) {
        webAudioSeek(time)
        return
      }

      if (!html5AudioRef.current) return

      html5AudioRef.current.currentTime = time
      setHtml5CurrentTime(time)
    },
    [useWebAudioMode, webAudioSeek],
  )

  // モード切り替え処理（Web Audio API ⇔ HTML5 audio）
  const switchAudioMode = useCallback(
    async (newSpeed: number) => {
      const previousMode = playbackSpeed === 1.0
      const newMode = newSpeed === 1.0

      // モード変更がない場合
      if (previousMode === newMode) {
        // HTML5 audio使用時はplaybackRateを更新
        if (!newMode && html5AudioRef.current) {
          html5AudioRef.current.playbackRate = newSpeed
        }
        return
      }

      // 現在の状態を保存
      const wasPlaying = isPlaying
      const savedTime = currentTime

      console.log('[Mode Switch]', {
        from: previousMode ? 'Web Audio' : 'HTML5',
        to: newMode ? 'Web Audio' : 'HTML5',
        wasPlaying,
        savedTime,
        newSpeed,
      })

      // 旧モードを停止
      if (wasPlaying) {
        pause()
      }

      // HTML5 audioモードに切り替える場合
      if (!newMode) {
        if (!html5AudioRef.current || !html5IsReady) {
          // HTML5 audio要素がまだ未マウントまたは未ロード: 状態を保存してonLoadedMetadataで復元
          console.log('[Mode Switch] HTML5 audio not ready, saving pending state...')
          pendingModeSwitchRef.current = { wasPlaying, savedTime, newSpeed }
          return
        }

        html5AudioRef.current.playbackRate = newSpeed
        html5AudioRef.current.currentTime = savedTime

        if (wasPlaying) {
          await html5AudioRef.current.play()
        }
        return
      }

      // Web Audio APIモードに切り替える場合
      // forceHTML5Mode が true（iOS Safari 等で Web Audio が失敗済み）の場合は切り替えない
      if (newMode && isAudioReady && !forceHTML5Mode) {
        webAudioSeek(savedTime)

        if (wasPlaying) {
          webAudioPlay()
        }
      }
    },
    [
      playbackSpeed,
      isPlaying,
      currentTime,
      html5IsReady,
      isAudioReady,
      forceHTML5Mode,
      pause,
      webAudioSeek,
      webAudioPlay,
    ],
  )

  // HTML5 audioが読み込まれたら、保存された状態を復元
  const restoreHtml5AudioState = useCallback(() => {
    // この関数は handleLoadedMetadata（音声ロード完了時）からのみ呼ばれるため、
    // html5IsReady は setHtml5IsReady(true) 呼び出し直後でまだ false のままの場合がある。
    // そのためガード節に html5IsReady を使わない。
    if (useWebAudioMode) return
    if (!html5AudioRef.current) return

    // モード切替時に保存した状態があればそれを優先（1.0x→他の倍率への最初の切り替え対応）
    const pending = pendingModeSwitchRef.current
    pendingModeSwitchRef.current = null

    // pending が null の場合はページ初回ロード時（速度変更なし）のため、
    // クロージャ内の currentTime / isPlaying / playbackSpeed はフック現在値として正しい。
    const savedTime = pending?.savedTime ?? currentTime
    const wasPlaying = pending?.wasPlaying ?? isPlaying
    const speed = pending?.newSpeed ?? playbackSpeed

    console.log('[HTML5 Audio Ready] Restoring state:', { savedTime, wasPlaying, speed, hadPending: !!pending })

    html5AudioRef.current.playbackRate = speed
    html5AudioRef.current.currentTime = savedTime

    if (wasPlaying) {
      html5AudioRef.current.play()
    }
  }, [useWebAudioMode, currentTime, isPlaying, playbackSpeed])

  // HTML5 audioイベントハンドラ
  const handleLoadedMetadata = useCallback(
    (e: React.SyntheticEvent<HTMLAudioElement>) => {
      const audio = e.currentTarget
      setHtml5Duration(audio.duration)
      setHtml5IsReady(true)
      restoreHtml5AudioState()
      console.log('[HTML5 Audio] Loaded, duration:', audio.duration)
    },
    [restoreHtml5AudioState],
  )

  const handleTimeUpdate = useCallback((e: React.SyntheticEvent<HTMLAudioElement>) => {
    setHtml5CurrentTime(e.currentTarget.currentTime)
  }, [])

  const handlePlay = useCallback(() => {
    setHtml5IsPlaying(true)
  }, [])

  const handlePause = useCallback(() => {
    setHtml5IsPlaying(false)
  }, [])

  const handleEnded = useCallback(() => {
    setHtml5IsPlaying(false)
    setHtml5CurrentTime(html5Duration)
  }, [html5Duration])

  const handleError = useCallback((e: React.SyntheticEvent<HTMLAudioElement>) => {
    console.error('[HTML5 Audio] Load error:', e)
    return { shouldFallback: true }
  }, [])

  // Web Audio ロード失敗時（EncodingError等）は自動的にHTML5モードにフォールバック
  useEffect(() => {
    if (webAudioError) {
      console.warn('[useAdaptiveAudio] Web Audio load failed, falling back to HTML5 audio:', webAudioError)
      setForceHTML5Mode(true)
    }
  }, [webAudioError])

  // iOS対策: forceHTML5Modeに切り替わった時、保留中の再生リクエストを実行
  useEffect(() => {
    if (forceHTML5Mode && html5IsReady && pendingPlayRef.current) {
      const pendingPlay = pendingPlayRef.current
      pendingPlayRef.current = null

      console.log('[useAdaptiveAudio] HTML5 audio is ready, executing pending play request...')

      if (html5AudioRef.current) {
        if (pendingPlay.offset !== undefined) {
          html5AudioRef.current.currentTime = pendingPlay.offset
          console.log('[useAdaptiveAudio] Set currentTime to:', pendingPlay.offset)
        }

        html5AudioRef.current
          .play()
          .then(() => {
            console.log('✅ [useAdaptiveAudio] HTML5 audio auto-play successful after fallback!')
          })
          .catch((err) => {
            console.error('❌ [useAdaptiveAudio] HTML5 audio auto-play failed:', err)
            console.error('   Please click the play button again.')
          })
      }
    }
  }, [forceHTML5Mode, html5IsReady])

  return {
    // 統一されたインターフェース
    isReady,
    isPlaying,
    duration,
    currentTime,
    play,
    pause,
    seek,
    switchAudioMode,
    initAudioContext, // iOS対策用の初期化関数

    // HTML5 audio用（JSXで使用）
    html5AudioRef,
    useWebAudioMode,
    html5EventHandlers: {
      onLoadedMetadata: handleLoadedMetadata,
      onTimeUpdate: handleTimeUpdate,
      onPlay: handlePlay,
      onPause: handlePause,
      onEnded: handleEnded,
      onError: handleError,
    },
  }
}
